import { Phase } from './Phases';

export type TutorialCardSelection =
  | {
      type: 'trade_row_card';
      defID: string;
    }
  | {
      type: 'basic_card';
      defID: string;
    };
// | {
//     type: 'tradeRowIndex';
//     tradeRowIndex: number;
//   };

export type EnforcedPlayerPlanningAction = {
  phase: Phase.PLANNING;
  cardSelection: TutorialCardSelection;
  militaryBid?: number;
};
export type EnforcedPlayerResolutionAction = {
  phase: Phase.RESOLUTION;
  cardSelection: TutorialCardSelection;
};

export enum TutorialHighlightType {
  CARD_ANATOMY,
  TRIBUTE_ANATOMY,
  PLAYER_SUMMARY,
  GAME_LOG,
  BIDDER,
  GLOBAL_BOARD,
}
export enum TutorialHighlightElementCardAnatomy {
  ENTIRE_CARD,
  CARD_NAME,
  CARD_COST,
  CARD_OBTAIN,
  CARD_TYPE,
  CARD_AFFINITY,
  CARD_EFFECTS,
  CARD_FAVOR,
  CARD_PROPHECY,
}
export enum TutorialHighlightElementPlayerSummary {
  PLAYER_NAME,
  PLAYER_FAVOR,
  PLAYER_RESOURCES,
  PLAYER_TYPE_COUNTS,
}
export enum TutorialHighlightElementGlobalBoard {
  TRADE_ROW,
  TRIBUTES,
  BASIC_CARDS,
}
export type TutorialTooltip = {
  text: string;
  location?: 'top' | 'bottom'; // default is top
} & (
  | {
      highlightType?: undefined;
    }
  | {
      highlightType: TutorialHighlightType.CARD_ANATOMY;
      cardDefID: string;
      highlightedElements: TutorialHighlightElementCardAnatomy[];
    }
  | {
      highlightType: TutorialHighlightType.TRIBUTE_ANATOMY;
      tributeDefID: string;
    }
  | {
      highlightType: TutorialHighlightType.PLAYER_SUMMARY;
      highlightedElements: TutorialHighlightElementPlayerSummary[];
    }
  | {
      highlightType: TutorialHighlightType.BIDDER;
      highlightedElements: ['resources'];
    }
  | {
      highlightType: TutorialHighlightType.GLOBAL_BOARD;
      highlightedElements: TutorialHighlightElementGlobalBoard[];
    }
  | {
      highlightType: TutorialHighlightType.GAME_LOG;
      elementKey: string;
    }
);

// Data for the tutorial game
// If a player action is not specified, the player can make any choice.
// If a bot action is not specified, the bot will default to EasyBot behavior.
// If rolls are not specified, the rolls will be random.
export type TutorialTurnData = {
  age: number;
  turn: number;
  playerPlanningAction?: EnforcedPlayerPlanningAction;
  playerResolutionAction?: EnforcedPlayerResolutionAction;
  botPlanningAction?: EnforcedPlayerPlanningAction;
  botResolutionAction?: EnforcedPlayerResolutionAction;
  playerRolls?: number[];
  botRolls?: number[];
  planningMessages?: TutorialTooltip[];
  resolutionMessages?: TutorialTooltip[];
};

const TUTORIAL: TutorialTurnData[] = [
  {
    age: 1,
    turn: 1,
    planningMessages: [
      // TODO: move these to different phases before planning
      {
        text: 'Welcome to Mythos! This tutorial will teach you the basics of the game and how to use the online interface. For the first few turns, you will be guided through selections. After that, it will be up to you to defeat the opponent!',
      },
      {
        text: 'Mythos is divided into 3 ages of 5 turns each. At the end of the game, the player who has gathered the most $FAVOR Favor wins the game.',
      },
      {
        text: 'You begin the game with a Leader card. This card grants you 2$GOLD Gold and 2$MILITARY Military at the beginning of the game, and 1 of each during each Unit phase at the start of the turn. $GOLD and $MILITARY are the two primary resources collected in Mythos that will help you obtain cards and further build your empire.',
        cardDefID: 'leader',
        highlightType: TutorialHighlightType.CARD_ANATOMY,
        highlightedElements: [TutorialHighlightElementCardAnatomy.ENTIRE_CARD],
      },
      {
        text: 'Each turn is composed of several phases. The online client will take care of most of these for you, but you will need to make decisions during two of them: Planning and Resolution.',
      },
      // {
      //   text: 'During the Setup Phase, cards are drawn from the Trade Row deck of the current age and placed on the Trade Row. The number of cards on a Trade Row is always equal to the number of players plus one. Tribute Cards are drawn from the Tribute deck until the Tribute Row has 3 cards on it.',
      // },
      {
        // XXX: this should probably highlight the counters in the bidder area, it is more intuitive than the player summary
        text: 'Before each Planning Phase, each player gains $GOLD and $MILITARY from the $PRODUCTION Unit effects granted by their cards, like your Leader. You now have 3$GOLD and 3$MILITARY, shown here.',
        highlightType: TutorialHighlightType.BIDDER,
        highlightedElements: ['resources'],
      },
      {
        text: 'During the Planning Phase, you will secretly choose one card from the Trade Row that you wish to obtain, and how much $MILITARY you would like to spend to fight over it. There will always be a number of cards on the Trade Row equal to the number of players plus one.',
        highlightType: TutorialHighlightType.GLOBAL_BOARD,
        highlightedElements: [TutorialHighlightElementGlobalBoard.TRADE_ROW],
      },
      {
        text: 'Card selections and bids are made in secret. If multiple players choose the same card, then they will clash over it, using their $MILITARY bid. We will cover that mechanic shortly.',
      },
      {
        text: "Let's take a look at one of the trade row cards, the Villager.",
        cardDefID: 'villager',
        highlightType: TutorialHighlightType.CARD_ANATOMY,
        highlightedElements: [TutorialHighlightElementCardAnatomy.ENTIRE_CARD],
      },
      {
        text: 'The Villager costs 1$GOLD to obtain - you have 3$GOLD right now, so it is affordable.',
        cardDefID: 'villager',
        highlightType: TutorialHighlightType.CARD_ANATOMY,
        highlightedElements: [TutorialHighlightElementCardAnatomy.CARD_COST],
      },
      {
        text: "The two symbols on the bottom right indicate the card's Type and Affinity.",
        cardDefID: 'villager',
        highlightType: TutorialHighlightType.CARD_ANATOMY,
        highlightedElements: [
          TutorialHighlightElementCardAnatomy.CARD_TYPE,
          TutorialHighlightElementCardAnatomy.CARD_AFFINITY,
        ],
      },
      {
        text: 'There are three Types in Mythos: $UNIT Unit, $BUILDING Building, and $EVENT Event. These Types describe how they grant you resources. Like the Leader, this Villager is a $UNIT, and will grant you resources during each Production phase.',
        cardDefID: 'villager',
        highlightType: TutorialHighlightType.CARD_ANATOMY,
        highlightedElements: [TutorialHighlightElementCardAnatomy.CARD_TYPE],
      },
      {
        text: 'There are three Gods in Mythos that cards can have Affinity with: $DERION Derion, $TYROS Tyros, and $ZEONA Zeona. These Gods skew towards different resources and strategies to create your path to victory. This Villager is a $DERION card. They frequently give $GOLD.',
        cardDefID: 'villager',
        highlightType: TutorialHighlightType.CARD_ANATOMY,
        highlightedElements: [
          TutorialHighlightElementCardAnatomy.CARD_AFFINITY,
        ],
      },
      {
        text: "The Villager's effect is listed at the bottom of the card. It grants you one 1$GOLD during the Unit phase of each turn.",
        cardDefID: 'villager',
        highlightType: TutorialHighlightType.CARD_ANATOMY,
        highlightedElements: [TutorialHighlightElementCardAnatomy.CARD_EFFECTS],
      },
      {
        text: 'The Villager is a good card to get your economy going, so select it for this turn. Additionally, bid 3$MILITARY to increase the chances that you will get to obtain it if your opponent makes the same choice.',
      },
    ],
    resolutionMessages: [
      // TODO: Move these to War Phase
      {
        text: 'The opponent also chose the villager, but they bid 0$MILITARY! The 3$MILITARY you bid will give you a strong advantage.',
        highlightType: TutorialHighlightType.GAME_LOG,
        elementKey: '21', // BID events group
      },
      {
        text: 'However, victory is not yet guaranteed. To determine the winner, each player who clashed over a card will now roll a 6-sided die and add their bid. The player with the highest total wins the clash.',
      },
      {
        text: 'You bid 3$MILITARY and rolled a 3, for a total of 6. Your opponent bid 0$MILITARY and rolled a 4. You have the higher total, so you have won the clash!',
        highlightType: TutorialHighlightType.GAME_LOG,
        elementKey: '24', // CONFLICT_RESULTS event group
      },
      {
        text: 'Since you bid 3$MILITARY, that 3$MILITARY is automatically deducted from your resources. This would be deducted whether you win or lose the clash.',
      },
      {
        text: 'It is now the Resolution phase. Since you won the clash, you may now obtain the Villager for 1$GOLD. Press Ready to continue.',
      },
      // 'After the Resolution phase, there is the Tribute phase. We will cover that phase in a later turn.',
      // 'Finally, the Cleanup phase happens. This phase prepares the game for the next turn. In a two-player game, all Trade Row cards are removed, and then replaced at the beginning of the next turn.',
    ],
    playerPlanningAction: {
      phase: Phase.PLANNING,
      cardSelection: {
        type: 'trade_row_card',
        defID: 'villager',
      },
      militaryBid: 3,
    },
    playerResolutionAction: {
      phase: Phase.RESOLUTION,
      cardSelection: {
        type: 'trade_row_card',
        defID: 'villager',
      },
    },
    botPlanningAction: {
      phase: Phase.PLANNING,
      cardSelection: {
        type: 'trade_row_card',
        defID: 'villager',
      },
      militaryBid: 0,
    },
    botResolutionAction: {
      phase: Phase.RESOLUTION,
      cardSelection: {
        type: 'basic_card',
        defID: 'scavenge_i',
      },
    },
    playerRolls: [3],
    botRolls: [4],
  },
  {
    age: 1,
    turn: 2,
    planningMessages: [
      {
        text: 'It is now Turn 2. In a two-player game, the Trade Row is completely refreshed each turn. In games with more players, only the cards that were obtained are replaced.',
      },
      {
        text: 'Resources are once again produced at the start of the turn. Since you now have the Villager, you produce 2$GOLD and 1$MILITARY, for a total of 4$GOLD and 1$MILITARY.',
        highlightType: TutorialHighlightType.GAME_LOG,
        elementKey: '39', // Turn 2 PRODUCTION events group
      },
      {
        text: "Let's now get a $TYROS Tyros card, the Axeman. $TYROS cards contribute towards $MILITARY production, which will be helpful for you to rebuild your $MILITARY from spending it last turn.",
      },
      {
        text: 'The Axeman has an additional effect, an Obtain effect. When you obtain it, you will get the resources listed under its cost - in this case, 4$MILITARY.',
        cardDefID: 'axeman',
        highlightType: TutorialHighlightType.CARD_ANATOMY,
        highlightedElements: [TutorialHighlightElementCardAnatomy.CARD_OBTAIN],
      },
      {
        text: 'Try to save your $MILITARY this turn, and bid 0 on Axeman. Then press READY.',
      },
    ],
    resolutionMessages: [
      {
        text: 'Unfortunately, this time, your foe had the upper hand! They have defeated you in the clash, so you will not be able to obtain the Axeman.',
      },
      // TODO highlight basic card area
      {
        text: 'However, not all is lost. Whenever you lose a clash, you will obtain a Basic card. These cards are infinite in supply. You may choose to obtain one instead of your Trade Row selection, even if you won the clash.',
        highlightType: TutorialHighlightType.GLOBAL_BOARD,
        highlightedElements: [TutorialHighlightElementGlobalBoard.BASIC_CARDS],
      },
      {
        text: 'There are three options for basic cards.',
        highlightType: TutorialHighlightType.GLOBAL_BOARD,
        highlightedElements: [TutorialHighlightElementGlobalBoard.BASIC_CARDS],
      },
      {
        text: 'Scavenge is free, and grants scaling $GOLD and $MILITARY based on the Age. It is an $EVENT Event card - $EVENT cards primarily grant resources through Obtain effects.',
        cardDefID: 'scavenge_i',
        highlightType: TutorialHighlightType.CARD_ANATOMY,
        highlightedElements: [],
      },
      {
        text: 'Settlement is a $BUILDING Building card - these cards grant $TOWN Towns and $FORT Forts, which give you discounts on the resources you spend.',
        cardDefID: 'settlement_i',
        highlightType: TutorialHighlightType.CARD_ANATOMY,
        highlightedElements: [],
      },
      {
        text: 'By buying the Settlement, you get 1 $TOWN, which reduces the cost of any further $GOLD purchases you make by 1.',
        cardDefID: 'settlement_i',
        highlightType: TutorialHighlightType.CARD_ANATOMY,
        highlightedElements: [TutorialHighlightElementCardAnatomy.CARD_EFFECTS],
      },
      {
        text: 'Settlement also has an obtain effect that grants 2 $GOLD for each other $BASIC Basic card you already own when you obtain it.',
        cardDefID: 'settlement_i',
        highlightType: TutorialHighlightType.CARD_ANATOMY,
        highlightedElements: [TutorialHighlightElementCardAnatomy.CARD_OBTAIN],
      },
      {
        text: 'The Mercenary is a $MILITARY producing $UNIT Unit that also grants some $MILITARY when you obtain it. It has 4 options you can choose from that cost different amounts of $GOLD, which you can see if you select the Mercenary during the Resolution phase.',
        cardDefID: 'mercenary_1_1',
        highlightType: TutorialHighlightType.CARD_ANATOMY,
        highlightedElements: [],
      },
      {
        text: 'Choose the Settlement this turn to develop your economy. Then press READY.',
      },
    ],
    playerPlanningAction: {
      phase: Phase.PLANNING,
      cardSelection: {
        type: 'trade_row_card',
        defID: 'axeman',
      },
      militaryBid: 0,
    },
    playerResolutionAction: {
      phase: Phase.RESOLUTION,
      cardSelection: {
        type: 'basic_card',
        defID: 'settlement_i',
      },
    },
    botPlanningAction: {
      phase: Phase.PLANNING,
      cardSelection: {
        type: 'trade_row_card',
        defID: 'axeman',
      },
      militaryBid: 4,
    },
    botResolutionAction: {
      phase: Phase.RESOLUTION,
      cardSelection: {
        type: 'trade_row_card',
        defID: 'axeman',
      },
    },
    playerRolls: [1],
    botRolls: [6],
  },
  {
    age: 1,
    turn: 3,
    planningMessages: [
      {
        text: 'This turn, we will cover Tributes. Tributes are missions given by the Gods. By completing them, you will gain Favor. Each Tribute is worth 3$FAVOR. There are always 3 Tributes available to complete.',
        location: 'bottom',
        highlightType: TutorialHighlightType.GLOBAL_BOARD,
        highlightedElements: [TutorialHighlightElementGlobalBoard.TRIBUTES],
      },
      {
        text: 'Tributes oftentimes have a simple condition to meet for completion. For example, the Grow Faith Tribute says "Obtain a $ZEONA Trade Row card."',
        highlightType: TutorialHighlightType.TRIBUTE_ANATOMY,
        tributeDefID: 'grow_faith',
        location: 'bottom',
      },
      {
        text: 'In the online interface, when you select a card, it will highlight all Tributes that could be completed by that selection.',
        location: 'bottom',
      },
      {
        text: 'Select the Guard Tower card. Be warned, Guard Tower fulfilling a Tribute means other players will want it, too. Choose your bid, then press READY.',
      },
    ],
    resolutionMessages: [
      {
        text: 'Your opponent chose a different card, so there are no clashes this turn. Both of you will be able to obtain your chosen card. Even if you do not fight, you will spend the $MILITARY you bid, so choose your bids carefully!',
      },
      {
        text: 'The Guard Tower is a Building that grants a $FORT Fort. Each $FORT reduces the amount of $MILITARY you must pay for your bid by 1. Therefore, your minimum bid online will always be set to the number of $FORT you have.',
      },
      {
        text: 'The Guard Tower also grant $FAVOR Favor. Many cards will grant $FAVOR in one of two ways. If the $FAVOR listed is just a number, it is called Base Favor. The other kind of Favor effect, Prophecies, will be covered later.',
        cardDefID: 'guard_tower',
        highlightType: TutorialHighlightType.CARD_ANATOMY,
        highlightedElements: [TutorialHighlightElementCardAnatomy.CARD_FAVOR],
      },
      {
        text: 'During the Tribute Phase each of the Tributes are evaluated to see which ones have been completed. In this case, you have completed Grow Faith - and because of that, you have also completed Show Piety. You will receive 1$TRIBUTE = 3$FAVOR for each Tribute completed.',
        location: 'bottom',
      },
      {
        text: 'Multiple players may be able to complete a Tribute during a turn. Each Tribute that is completed will be discarded and replaced at the end of turn. Press READY to gain Guard Tower.',
        location: 'bottom',
      },
    ],
    playerPlanningAction: {
      phase: Phase.PLANNING,
      cardSelection: {
        type: 'trade_row_card',
        defID: 'guard_tower',
      },
    },
    playerResolutionAction: {
      phase: Phase.RESOLUTION,
      cardSelection: {
        type: 'trade_row_card',
        defID: 'guard_tower',
      },
    },
    botPlanningAction: {
      phase: Phase.PLANNING,
      cardSelection: {
        type: 'trade_row_card',
        defID: 'mining_camp',
      },
      militaryBid: 1,
    },
    botResolutionAction: {
      phase: Phase.RESOLUTION,
      cardSelection: {
        type: 'trade_row_card',
        defID: 'mining_camp',
      },
    },
  },
  {
    age: 1,
    turn: 4,
    planningMessages: [
      {
        text: "Some cards have Prophecies. Prophecies grant $FAVOR based on the cards you acquire throughout the game. They will be listed in the card's favor area.",
      },
      {
        text: 'Growth has a Prophecy that grants 1$FAVOR for each $DERION Derion card you own. Online, Prophecy Favor is automatically added to your total $FAVOR.',
        cardDefID: 'growth',
        highlightType: TutorialHighlightType.CARD_ANATOMY,
        highlightedElements: [TutorialHighlightElementCardAnatomy.CARD_FAVOR],
      },
      {
        text: 'Select Growth and set your bid. Notice that your minimum bid is now 1 because of the $FORT you acquired. Then press READY.',
      },
    ],
    playerPlanningAction: {
      phase: Phase.PLANNING,
      cardSelection: {
        type: 'trade_row_card',
        defID: 'growth',
      },
    },
    playerResolutionAction: {
      phase: Phase.RESOLUTION,
      cardSelection: {
        type: 'trade_row_card',
        defID: 'growth',
      },
    },
    botPlanningAction: {
      phase: Phase.PLANNING,
      cardSelection: {
        type: 'trade_row_card',
        defID: 'growth',
      },
      militaryBid: 0,
    },
    botResolutionAction: {
      phase: Phase.RESOLUTION,
      cardSelection: {
        type: 'basic_card',
        defID: 'scavenge_i',
      },
    },
    playerRolls: [6],
    botRolls: [1],
  },
  {
    age: 1,
    turn: 5,
    planningMessages: [
      {
        text: "It is now the final turn of the age! You will retain all of your resources going into the next age, but the pool of Trade Row cards will change. Let's use this turn to build your economy to prepare for the next age.",
      },
      {
        text: 'The Mill of Derion card is a card with an $UPGRADE Upgrade effect - it always has one effect, listed at the bottom, and then a second effect listed above which is active once the Upgrade condition is met.',
        cardDefID: 'mill_of_derion',
        highlightType: TutorialHighlightType.CARD_ANATOMY,
        highlightedElements: [TutorialHighlightElementCardAnatomy.CARD_EFFECTS],
      },
      {
        text: 'Mill of Derion grants one $TOWN, and then a second $TOWN once you have 4$DERION cards, including itself. Because you already own 2$DERION cards, this is a strong, synergistic card with your board.',
        cardDefID: 'mill_of_derion',
        highlightType: TutorialHighlightType.CARD_ANATOMY,
        highlightedElements: [],
      },
      {
        text: 'Select Mill of Derion and set your bid. Then press READY.',
      },
    ],
    playerPlanningAction: {
      phase: Phase.PLANNING,
      cardSelection: {
        type: 'trade_row_card',
        defID: 'mill_of_derion',
      },
    },
    playerResolutionAction: {
      phase: Phase.RESOLUTION,
      cardSelection: {
        type: 'trade_row_card',
        defID: 'mill_of_derion',
      },
    },
    botPlanningAction: {
      phase: Phase.PLANNING,
      cardSelection: {
        type: 'trade_row_card',
        defID: 'soldier_of_tyros',
      },
    },
    botResolutionAction: {
      phase: Phase.RESOLUTION,
      cardSelection: {
        type: 'trade_row_card',
        defID: 'soldier_of_tyros',
      },
    },
  },
  {
    age: 2,
    turn: 1,
    planningMessages: [
      {
        text: 'It is now Age II. Trade Row cards are generally more expensive, but more powerful. It is an important age to solidify your strategy and develop your economy. Because it is Age II, two dice are rolled in combat.',
      },
      {
        text: 'You may find a few online elements helpful to strategize: the Player Summary View and the Game Log.',
      },
      {
        text: "The Player Summary View contains a summary of each player's board state and resources.",
        highlightType: TutorialHighlightType.PLAYER_SUMMARY,
        highlightedElements: [],
      },
      {
        text: "This area lists the player's $FAVOR. You may click it to toggle viewing a breakdown of the player's $FAVOR between Favor from Tributes, Base Favor, and Prophecy Favor.",
        highlightType: TutorialHighlightType.PLAYER_SUMMARY,
        highlightedElements: [
          TutorialHighlightElementPlayerSummary.PLAYER_FAVOR,
        ],
      },
      {
        text: "This area lists the player's resources. $GOLD and $MILITARY are listed here, as well as their $TOWN and $FORT, and per-turn Production.",
        highlightType: TutorialHighlightType.PLAYER_SUMMARY,
        highlightedElements: [
          TutorialHighlightElementPlayerSummary.PLAYER_RESOURCES,
        ],
      },
      {
        text: 'Clicking on this toggles between viewing their resource count as just their current resources, or resources and buildings combined. You may find the latter view helpful if you are trying to see the maximum spending potential they have.',
        highlightType: TutorialHighlightType.PLAYER_SUMMARY,
        highlightedElements: [
          TutorialHighlightElementPlayerSummary.PLAYER_RESOURCES,
        ],
      },
      {
        text: 'This area gives a breakdown of each of the card Type and Affinity counts for each player. Each Type and Affinity also lists whether or not the player has Prophecies, indicated by one or more purple pips, or an unfulfilled upgrade, indicated by the $UPGRADE icon.',
        highlightType: TutorialHighlightType.PLAYER_SUMMARY,
        highlightedElements: [
          TutorialHighlightElementPlayerSummary.PLAYER_TYPE_COUNTS,
        ],
      },
      {
        text: 'You now have the basics of Mythos. Use what you have learned to defeat your opponent!',
      },
    ],
  },
  {
    age: 3,
    turn: 1,
    planningMessages: [
      {
        text: 'Age III is the final age of the game, and contains many powerful cards. Like Age II, yet another die is added to clashes, so you will now roll 3 dice!',
      },
    ],
  },
  {
    age: 3,
    turn: 5,
    planningMessages: [
      {
        text: 'It is the last turn of the game. It all comes down to this! Unspent resources are not worth any $FAVOR, so make sure to use what you can.',
      },
      {
        text: 'After this, the player with the most total $FAVOR will win the game. Make your final selections, and defeat your opponent.',
      },
    ],
  },
];

// The cards and tributes here are listed in order, i.e. the first card in the list will be the first card drawn in the game.
// Unlisted cards and tributes will be randomized and added to the deck below the listed cards.
export const TUTORIAL_CARD_ORDER_BY_AGE: { [age: number]: string[] } = {
  1: [
    'villager',
    'warrior',
    'pillage',
    'farm',
    'axeman',
    'altar_of_bounty',
    'mining_camp',
    'guard_tower',
    'trader',
    'growth',
    'horseman',
    'guard_hound',
    'prophet',
    'soldier_of_tyros',
    'mill_of_derion',
  ],
  2: ['acropolis', 'blessed_might', 'granary', 'ritual_of_growth'],
  3: ['citadel'],
};
export const TUTORIAL_TRIBUTE_ORDER: string[] = [
  'grow_faith',
  'grow_legend',
  'show_piety',
  'persevere',
  'all_in',
  'show_might',
  'bring_peace',
  'grow_conquest',
  'grow_community',
  'grow_bounty',
  'seek_wonder',
  'grow_polis',
  'seek_humility',
  'overpower',
  'impulsive_choice',
  'patient_choice',
];

export function getTutorialData(
  age: number,
  turn: number,
): TutorialTurnData | null {
  return (
    TUTORIAL.find((data) => data.age === age && data.turn === turn) || null
  );
}

export default {
  TUTORIAL,
};
